import { IItem } from "../interfaces/IItem";
import { min, max, subDays, addDays, isValid, differenceInCalendarYears, subYears, addYears, differenceInCalendarDays, differenceInDays, startOfDay, parseISO, addHours } from "date-fns";
import { ZOOM_MODE_DAY, ZOOM_MODE_MONTH, ZOOM_MODE_WEEK, ZOOM_MODE_YEAR } from "src/gantt/helpers/Const";
import { parseEndDateFromQueryString, parseStartDateFromQueryString } from "./urlHelper";

export const mergeDateAndTime = (d: Date, t: Date): Date => {
  if (d == null) {
    return null;
  }

  if (t == null) {
    return d;
  }

  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), t.getHours(), t.getMinutes(), t.getSeconds());
};

export const getLargestDateRangeFromItems = (items: IItem[], maxDateRangeInYears: number): [Date, Date] => {
  const startDates = items.map((i) => (i.start != null ? i.start : i.end)).filter((d) => d != null);
  const endDates = items.map((i) => (i.end != null ? i.end : i.start)).filter((d) => d != null);

  const medianStartDate = startDates[Math.floor(startDates.length / 2)];
  const medianEndDate = endDates[Math.floor(endDates.length / 2)];
  const midpoint = addDays(medianStartDate, Math.abs(Math.floor(differenceInCalendarDays(medianStartDate, medianEndDate) / 2)));

  //Check if array has values and round to dates
  const minStartDate = startDates?.length ? new Date(min(startDates).toDateString()) : null;
  const maxEndDate = endDates?.length ? new Date(max(endDates).toDateString()) : null;

  let dateRange: [Date, Date] = [subDays(Date.now(), 7), addDays(Date.now(), 7)];
  
  if (minStartDate != null && maxEndDate != null) {
    dateRange = [minStartDate, maxEndDate];
  } else if (minStartDate == null && maxEndDate != null) {
    dateRange = [subDays(maxEndDate, 14), maxEndDate];
  } else if (maxEndDate == null && minStartDate != null) {
    dateRange = [minStartDate, addDays(minStartDate, 14)];
  }

  // In case the data contains some abnormally early start or late end date, limit the date range
  // Real life example: user wanted a task to last practically forever so the end date was set to year 5435
  const differenceInYears = differenceInCalendarYears(dateRange[1], dateRange[0]);
  if (Math.abs(differenceInYears) > maxDateRangeInYears) {
    dateRange = [
      subYears(midpoint, Math.floor(maxDateRangeInYears / 2)),
      addYears(midpoint, maxDateRangeInYears - Math.floor(maxDateRangeInYears / 2)),
    ];
  }

  //Having start and end being same causes problems in rendering, add one day to end as workaround
  if(dateRange[0].getTime() === dateRange[1].getTime()) {
    dateRange[1] = addDays(dateRange[1],1);
  }

  return dateRange;
};

// Ensures an item has valid start and end dates.
// Default to a one day length.
// Indicate somehow to the user that the item's dates are invalid
// when durationUnit is "day" then duration = 1 if defaultDuration is not set
export const ensureStartAndEnd = (start: Date, end: Date, defaultDate?: Date, defaultDuration?: number, durationUnit?: string): [Date, Date] => {
  
  const hasStart = isValid(start);
  const hasEnd   = isValid(end);
  const duration = defaultDuration > 0 ? defaultDuration : 1;

  if (hasStart && hasEnd && end > start) {

    return [start, end];

  } else if (hasStart && hasEnd && start >= end) {

    return [start, addDays(start, duration)];

  } else if (hasStart && !hasEnd) {

    if ( durationUnit && durationUnit === "h" ) {
      // hours are in decimal between 0 and 1, so we recalculate the number of hours to be equal number
      return [start, addHours(start, (duration * 24) )];
    }

    return [start, addDays(start, duration)];

  } else if (!hasStart && hasEnd) {

    return [subDays(end, duration), end];

  }

  if (defaultDate) {

    return [defaultDate, addDays(defaultDate, duration)];

  } else {

    return [null, null];

  }
};

export const calculateZoomModeFromDateRange = (endDate: Date, startDate: Date): string => {
  let duration = differenceInDays(startDate, endDate);
  if(duration < 0) {
    duration = duration * -1;
  }
  let zoomMode = ZOOM_MODE_YEAR;
  if (duration < 7) {
    zoomMode = ZOOM_MODE_DAY;
  } else if (duration < 27) {
    zoomMode = ZOOM_MODE_WEEK;
  } else if (duration < 150) {
    zoomMode = ZOOM_MODE_MONTH;
  }
  return zoomMode;
}

const initialDate = (urlParam: string, defaultValue: Date): Date => {
  if(urlParam?.match('^-?\\d+$')) {
    return addDays(startOfDay(new Date()), parseInt(urlParam));
  }
  if(urlParam?.match('^\\d{4}-\\d{2}-\\d{2}$')) {
    return startOfDay(parseISO(urlParam));
  }
  return defaultValue;
}

export const initialStartDate = (): Date => {
  return initialDate(parseStartDateFromQueryString(),subDays(startOfDay(new Date()), 15));
}

export const initialEndDate = (): Date => {
  return initialDate(parseEndDateFromQueryString(),addDays(startOfDay(new Date()), 16));
}